import { type FC, type ReactElement, useCallback, useState } from "react"

import { Navigate } from "react-router-dom"

import {
    ETypographyAlignment,
    ETypographyColor,
    ETypographyFamily,
    ETypographySize,
    ETypographyTag,
    ETypographyWeight,
    Typography
} from "@/3514/components"
import { type TScreenBreakpoints, type TUserState, useScreenBreakpoints, useUser } from "@/3514/hooks"
import { ParticipantLayout } from "@/3514/layouts"
import { EUserRole } from "@/3514/types"
import { buildCssClassName } from "@/3514/utils"
import { INDEX_URL } from "@/constants"
import { OldBeliefGoal } from "@/entities/old-belief-goal"
import { type IUseTranslation, useTranslation } from "@/hooks"
import type { TEmptyCallback } from "@/shared/types/functions"
import { BehavioralGoals } from "@/widgets/behavioral-goal/ui/BehavioralGoal"
import CoachingGoal from "@/widgets/coaching-goal/ui/CoachingGoal"

import { GoalsWrapper } from "./ui/GoalsWrapper"

import "./ui/styles.scss"

export const CoachingPlanPage: FC = (): ReactElement => {
    const { user }: TUserState<EUserRole.Participant> = useUser<EUserRole.Participant>()

    const { t }: IUseTranslation = useTranslation()

    const breakpoints: TScreenBreakpoints = useScreenBreakpoints()

    const isMobile: boolean = breakpoints.xs || breakpoints.sm

    const [isToggledOldBelief, setIsToggledOldBelief] = useState(isMobile)

    const toggleAside: TEmptyCallback = useCallback(
        (): void => setIsToggledOldBelief((prev: boolean): boolean => !prev),
        []
    )

    return user.isCoachingPlanAvailable ? (
        <ParticipantLayout>
            <div className="goals-layout">
                <Typography
                    text={t("participantSide.sidebar.list.plan.label")}
                    className="mb-0 mt-[40px]"
                    tag={ETypographyTag.Heading1}
                    alignment={ETypographyAlignment.Center}
                    size={ETypographySize.Heading1}
                    color={ETypographyColor.Black}
                    family={ETypographyFamily.DmSerif}
                    weight={ETypographyWeight.Medium}
                />

                <GoalsWrapper>
                    <div className={buildCssClassName("goals-grid", { toggled: isToggledOldBelief })}>
                        <section className={buildCssClassName("goals-main", { expanded: isToggledOldBelief })}>
                            <CoachingGoal />
                            <div className="mb-50" />
                            <BehavioralGoals />
                        </section>

                        <aside
                            aria-label={t("participantSide.coachPlanPage.oldBeliefsAside.ariaLabel")}
                            className={buildCssClassName("goal-aside", { toggled: isToggledOldBelief })}
                        >
                            <OldBeliefGoal toggle={toggleAside} isToggled={isToggledOldBelief} />
                        </aside>
                    </div>
                </GoalsWrapper>
            </div>
        </ParticipantLayout>
    ) : (
        <Navigate to={INDEX_URL} replace />
    )
}
